/**
 * API Response Schemas
 * Validation schemas for API responses
 */

import { z } from 'zod';
import { todoSchema } from '../todo.schemas';
import { clientSchema } from '../client.schemas';
import { projectSchema } from '../project.schemas';

/**
 * API Response Metadata
 */
export const apiResponseMetadataSchema = z.object({
  timestamp: z.string().datetime(),
  request_id: z.string().uuid().optional(),
  trace_id: z.string().optional(),
  version: z.string().optional(),
});

export type ApiResponseMetadata = z.infer<typeof apiResponseMetadataSchema>;

/**
 * Generic API Response Envelope
 * Standard structure for all API responses
 */
export function createApiResponseSchema<T extends z.ZodTypeAny>(dataSchema: T) {
  return z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
    metadata: apiResponseMetadataSchema.optional(),
  });
}

/**
 * Paginated Response Schema
 */
export function createPaginatedResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    pages: z.number().int().nonnegative(),
    has_next: z.boolean(),
    has_prev: z.boolean(),
  });
}

/**
 * Success Response Helper
 */
export function createSuccessResponseSchema<T extends z.ZodTypeAny>(dataSchema: T) {
  return z.object({
    success: z.literal(true),
    data: dataSchema,
    metadata: apiResponseMetadataSchema.optional(),
  });
}

/**
 * Error Response Schema
 */
export const errorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  details: z.array(z.object({
    field: z.string().optional(),
    message: z.string(),
    code: z.string().optional(),
  })).optional(),
  metadata: apiResponseMetadataSchema.optional(),
});

export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/**
 * Todo Response Schemas
 */
export const todoResponseSchema = createApiResponseSchema(todoSchema);
export const todosListResponseSchema = createApiResponseSchema(
  createPaginatedResponseSchema(todoSchema)
);

export type TodoResponse = z.infer<typeof todoResponseSchema>;
export type TodosListResponse = z.infer<typeof todosListResponseSchema>;

/**
 * Client Response Schemas
 */
export const clientResponseSchema = createApiResponseSchema(clientSchema);
export const clientsListResponseSchema = createApiResponseSchema(
  createPaginatedResponseSchema(clientSchema)
);

export type ClientResponse = z.infer<typeof clientResponseSchema>;
export type ClientsListResponse = z.infer<typeof clientsListResponseSchema>;

/**
 * Project Response Schemas
 */
export const projectResponseSchema = createApiResponseSchema(projectSchema);
export const projectsListResponseSchema = createApiResponseSchema(
  createPaginatedResponseSchema(projectSchema)
);

export type ProjectResponse = z.infer<typeof projectResponseSchema>;
export type ProjectsListResponse = z.infer<typeof projectsListResponseSchema>;

/**
 * Generic Success Response
 */
export const genericSuccessResponseSchema = createApiResponseSchema(z.object({
  message: z.string(),
}));

export type GenericSuccessResponse = z.infer<typeof genericSuccessResponseSchema>;

/**
 * Batch Operation Response
 */
export const batchOperationResponseSchema = createApiResponseSchema(z.object({
  total: z.number().int().nonnegative(),
  successful: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z.array(z.object({
    id: z.string(),
    error: z.string(),
  })).optional(),
}));

export type BatchOperationResponse = z.infer<typeof batchOperationResponseSchema>;
